import * as React from 'react';
import {
  Button,
  CssBaseline,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Box,
  Typography,
  Container,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import agent from '../api/axios';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup
    .string()
    .min(6, 'Min 6 characters')
    .required('Password is required'),
});

type LoginFormInputs = {
  email: string;
  password: string;
};

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {'© '}
      {new Date().getFullYear()}
      {' SimplyPayroll. All rights reserved.'}
    </Typography>
  );
}

export default function LoginPage() {
  const { login, logout } = useAuth();

  // Clear any stale auth state when visiting login page
  React.useEffect(() => {
    const flagKey = 'loginCleared';
    if (!sessionStorage.getItem(flagKey)) {
      // Use logout to properly clear both localStorage AND auth state
      logout();
      sessionStorage.setItem(flagKey, '1');
    }
  }, [logout]);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [forgotOpen, setForgotOpen] = React.useState(false);
  const [forgotEmail, setForgotEmail] = React.useState('');
  const [forgotLoading, setForgotLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(schema),
  });

  const handleRememberMeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRememberMe(event.target.checked);
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onSubmit = async (data: LoginFormInputs) => {
    try {
      setLoading(true);
      const { token } = await agent.Auth.login(data);

      // Update Redux auth state (sets isAuthenticated, stores token, parses role)
      login(token);

      // Also store in authToken key for rememberMe persistence
      if (rememberMe) {
        localStorage.setItem('authToken', token);
      } else {
        sessionStorage.setItem('authToken', token);
      }

      navigate('/dashboard');
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(error.response?.data?.message || 'Login failed', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendForgot = async () => {
    try {
      setForgotLoading(true);
      await agent.Auth.forgotPassword(forgotEmail);
      enqueueSnackbar(
        'If an account exists for that email, a reset link has been sent.',
        { variant: 'success' }
      );
      setForgotOpen(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to send reset email', { variant: 'error' });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #f8faf8 0%, #e8f5e9 100%)'
            : 'linear-gradient(135deg, #121212 0%, #1a1a1a 100%)',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.12)',
            p: 4,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/logo192.png"
            alt="SimplyPayroll"
            sx={{
              width: 64,
              height: 64,
              mb: 2,
            }}
          />
          <Typography
            component="h1"
            variant="h5"
            sx={{ fontWeight: 700, mb: 0.5 }}
          >
            SimplyPayroll
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to your account
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ width: '100%' }}
          >
            <TextField
              label="Email Address"
              fullWidth
              required
              margin="normal"
              autoComplete="email"
              autoFocus
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              margin="normal"
              autoComplete="current-password"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  value="remember"
                  color="primary"
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                />
              }
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                href="#"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setForgotOpen(true);
                }}
              >
                Forgot password?
              </Link>
            </Box>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                href="/register"
                variant="body2"
              >
                {"Don't have an account? Sign Up"}
              </Link>
            </Box>
          </Box>
        </Box>
        <Copyright sx={{ mt: 4 }} />
      </Container>
      <Dialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Forgot password</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            type="email"
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)} disabled={forgotLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendForgot}
            variant="contained"
            disabled={forgotLoading || !forgotEmail}
          >
            {forgotLoading ? 'Sending...' : 'Send reset link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
